/**
 * This file contains content for the help panel that can appear to the right of a list of autocomplete matches in the SearchHelper.
 *
 * The 'syntax' and 'example' fields can contain multiple strings. Any of those that begins with a space will be indented.
 * If the optional 'header' and 'rows' fields are present, a table of sample PQ results will be shown.
 */

import * as K from 'common/constants';

export type HelpItem = {
  name: string;
  description: string;
  syntax?: string | string[];
  example?: string | string[];
  summary?: string;
  header?: string[];
  rows?: string[][];
};

// graphing functions
export const FunctionInfo: Array<HelpItem> = [
  {
    name: 'count',
    description: 'The number of matching events',
    syntax: 'count(expr)',
    example: 'count(message contains "error") / count(message contains "success")',
    summary: 'number of matching events',
  },
  {
    name: 'fraction',
    description: 'The fraction (from 0 to 1) of events which match the given expression',
    syntax: 'fraction(expr)',
    example: 'fraction(message contains "error")',
    summary: 'fraction of events which match the given expression',
  },
  {
    name: 'max',
    description: 'Largest value',
    syntax: 'max(value)',
    example: 'max(bytes where serverHost="frontend-1")',
    summary: 'largest value',
  },
  {
    name: 'mean',
    description: 'Average value',
    syntax: 'mean(value)',
    example: 'mean(bytes where serverHost="frontend-1")',
    summary: 'average value',
  },
  {
    name: 'median',
    description: 'The median (50th percentile) value',
    syntax: 'median(value)',
    example: 'median(bytes where serverHost="frontend-1")',
    summary: 'median value',
  },
  {
    name: 'min',
    description: 'Smallest value',
    syntax: 'min(value)',
    example: 'min(bytes where serverHost="frontend-1")',
    summary: 'smallest value',
  },
  {
    name: 'p',
    description: 'The Nth percentile value',
    syntax: 'p[n](value)',
    example: 'p[85](bytes where serverHost="frontend-1")',
    summary: 'nth percentile value',
  },
  {
    name: 'p10',
    description: 'The 10th percentile value',
    syntax: 'p10(value)',
    example: 'p10(bytes where serverHost="frontend-1")',
    summary: '10th percentile value',
  },
  {
    name: 'p50',
    description: 'The 50th percentile value',
    syntax: 'p50(value)',
    example: 'p50(bytes where serverHost="frontend-1")',
    summary: '50th percentile value',
  },
  {
    name: 'p90',
    description: 'The 90th percentile value',
    syntax: 'p90(value)',
    example: 'p90(bytes where serverHost="frontend-1")',
    summary: '90th percentile value',
  },
  {
    name: 'p95',
    description: 'The 95th percentile value',
    syntax: 'p95(value)',
    example: 'p95(bytes where serverHost="frontend-1")',
    summary: '95th percentile value',
  },
  {
    name: 'p99',
    description: 'The 99th percentile value',
    syntax: 'p99(value)',
    example: 'p99(bytes where serverHost="frontend-1")',
    summary: '99th percentile value',
  },
  {
    name: 'p999',
    description: 'The 99.9th percentile value',
    syntax: 'p999(value)',
    example: 'p999(bytes where serverHost="frontend-1")',
    summary: '99.9th percentile value',
  },
  {
    name: 'sum',
    description: 'Sum of values',
    syntax: 'sum(value)',
    example: 'sum(bytes where serverHost="frontend-1")',
    summary: 'sum of values',
  },
  {
    name: 'sumPerSec',
    description:
      'Sum of values, divided by the number of seconds in the time period from which the values were obtained',
    syntax: 'sumPerSec(value)',
    example: 'sumPerSec(bytes where serverHost="frontend-1")',
    summary: 'sum of values divided by seconds',
  },
  {
    name: 'sumPerSecond',
    description:
      'Sum of values, divided by the number of seconds in the time period from which the values were obtained',
    syntax: 'sumPerSecond(value)',
    example: 'sumPerSecond(bytes where serverHost="frontend-1")',
    summary: 'sum of values divided by seconds',
  },
];

// Power Query commands
export const CommandInfo: Array<HelpItem> = [
  {
    name: 'columns',
    description:
      'Selects fields for display, renames or reorders fields, and/or computes new fields. A typical application is to place this command at the end of your query to clean up the query output, ' +
      'by renaming important fields, and discarding fields that were only needed for intermediate processing.',
    syntax: ['columns expression, expression2, …', 'columns field=expression, field2=expression2, …'],
    summary: 'fields to display',
    example: 'columns timestamp, isError = (status >= 500 && status <= 599), bytes',
    header: ['timestamp', 'isError', 'bytes'],
    rows: [
      ['Feb 5 · 07:13:22.809 am', 'false', '7808'],
      ['Mar 8 · 12:25:00.585 pm', 'true', '45'],
    ],
  },
  {
    name: 'filter',
    description: 'Discards records that do not satisfy the specified condition.',
    syntax: 'filter filterExpression',
    example: 'filter status == 502',
    summary: 'discard some records',
  },
  {
    name: 'group',
    description:
      'Aggregates records, grouping them by one or more fields, and computing aggregate statistics for each group. The following functions are supported: count(), sum(), avg(), min(), ' +
      'max(), median(), pct(), p10(), p50(), p90(), p99(), p999(), stddev(), estimate_distinct(), first(), last(), oldest(), newest(), and any(). ' +
      'count() can be used with no arguments to count all records, or with an argument to count the number of records where that argument is true, e.g. count(status == 404). ' +
      'pct() computes percentiles, e.g. pct(99.9, latency); p10(), p50(), etc. compute the 10th percentile, 50th percentile, etc.',
    syntax: [
      'group function(expression), function2(expression2), … by expression3, expression4, …',
      'group name=function(expression), name2=function2(expression2), … by name3=expression3, name4=expression4, …',
    ],
    example: [
      'group total = count(), errors = count(status >= 500 && status <= 599) by uriPath',
      'group count(), median(latency) by region, environment',
      'group estimate_distinct(ip)',
    ],
    summary: 'aggregate records by fields',
    header: ['uriPath', 'total', 'errors'],
    rows: [
      ['/subscribe', '17,153', '22'],
      ['/unsubscribe', '3,286', '75'],
    ],
  },
  {
    name: 'inner join',
    description:
      'Same as "join". Executes two or more subqueries, and merges their results into a single table. Only the data from records that match the provided rules will be included in the output.',
    syntax: [
      'inner join (query), (query2) on field',
      'inner join name = (query), name2 = (query2), … on name.field = name2.field2, …',
    ],
    example: [
      'inner join a = (status = 200 | columns tag), b = (numBytes > 1_000_000 | columns tag) on tag',
      'inner join',
      ' start = ( start_transaction | columns timestamp, transaction_id, operation_name),',
      ' end = ( end_transaction | columns timestamp, transaction_id, elapsed_time)',
      ' on transaction_id',
    ],
    summary: 'merge results from multiple queries',
  },
  {
    name: 'join',
    description:
      'Executes two or more subqueries, and merges their results into a single table. Only the data from records that match the provided rules will be included in the output.',
    syntax: [
      'join (query), (query2) on field',
      'join name = (query), name2 = (query2), … on name.field = name2.field2, …',
    ],
    example: [
      'join a = (status = 200 | columns tag), b = (numBytes > 1_000_000 | columns tag) on tag',
      'join',
      ' start = ( start_transaction | columns timestamp, transaction_id, operation_name),',
      ' end = ( end_transaction | columns timestamp, transaction_id, elapsed_time)',
      ' on transaction_id',
    ],
    summary: 'merge results from multiple queries',
  },
  {
    name: 'left join',
    description:
      'Executes two or more subqueries, and merges their results into a single table. Records from the first query and records that match the provided rules will be included in the output.',
    syntax: [
      'left join (query), (query2) on field',
      'left join name = (query), name2 = (query2), … on name.field = name2.field2, …',
    ],
    example: [
      'left join a = (status = 200 | columns tag), b = (numBytes > 1_000_000 | columns tag) on tag',
      'left join',
      ' start = ( start_transaction | columns timestamp, transaction_id, operation_name),',
      ' end = ( end_transaction | columns timestamp, transaction_id, elapsed_time)',
      ' on transaction_id',
    ],
    summary: 'merge results with those of the first',
  },
  {
    name: 'let',
    description:
      'Defines one or more new fields. Use "let" to perform computations on values from a raw event, values extracted by a "parse" command, or records created by a "group" command.',
    syntax: 'let field=expression, field2=expression2, …',
    example: 'let isError = (status >= 500 && status <= 599)',
    summary: 'define new fields',
  },
  {
    name: 'limit',
    description: 'Caps the number of records displayed or processed by subsequent commands.',
    syntax: 'limit [nnn]',
    example: 'limit 10',
    summary: 'cap the number of records shown',
  },
  {
    name: 'lookup',
    description: 'Retrieves values from a lookup table stored in a configuration file',
    syntax: 'lookup field=columnName, … from "tableName" by columnName=expression, …',
    example: 'lookup name from "users" by id=requestUserId',
    summary: 'retrieve values from configuration files',
  },
  {
    name: 'parse',
    description:
      'Extracts new fields from the specified field (or from the raw log message, if no field is specified).The format is ' +
      K.PRODUCT_NAME +
      "'s standard parser language, with a few minor limitations. Double escape regex characters.",
    syntax: ['parse [format]', 'parse [format] from fieldName'],
    example: [
      'parse "image conversion processed $size$KB in $time$ seconds" from summary',
      'parse "-$role$-" from machine',
      'parse "image conversion processed $size{regex=\\d+}$"',
      'parse "image conversion processed =\\d+$units$"',
    ],
    summary: 'extract new fields from a field',
    header: ['size', 'time'],
    rows: [
      ['1537', '1.03'],
      ['8774', '2,81'],
    ],
  },
  {
    name: 'sort',
    description:
      'Determines the order in which records are displayed. If you specify multiple sort columns, the one listed first takes precedence. "+" or "-" specify ascending or descending order; ' +
      'ascending is the default.',
    syntax: ['sort field, field2, …', 'sort +field, -field2, …'],
    example: 'sort -error_rate',
    summary: 'set the display order of records',
  },
  {
    name: 'transpose',
    description:
      'Removes a column from the table and creates a new column for each of its values. The values within the new columns are taken from the last column of the original table.',
    syntax: 'transpose field',
    example: 'transpose serverHost',
    summary: 'transpose a column',
    header: ['timestamp', 'prod-23a', 'prod-45b'],
    rows: [
      ['10:00 AM', '0.133', '0.109'],
      ['11:00 AM', '0.188', '0.074'],
      ['12:00 PM', '0.215', '0.086'],
    ],
  },
  {
    name: 'union',
    description:
      'Executes two or more subqueries, and merges their results into a single table. Each record from each subquery becomes a record in the output.',
    syntax: 'union (query), (query), …',
    example: [
      'union (status = 404 | columns tag), (severity > 3 | columns tag)',
      'union',
      ' (logfile="api_server" | columns operation, elapsed_time, status = status_code),',
      ' (logfile="frontend" | columns url, status = http_status)',
    ],
    summary: 'include records from multiple queries',
  },
];

export const PowerQueryNumericFunctionInfo: Array<HelpItem> = [
  {
    name: 'abs',
    description: 'The absolute value of x',
    syntax: 'abs(x)',
    example: 'abs(value)',
    summary: 'absolute value',
  },
  {
    name: 'ceiling',
    description: 'x, rounded up to an integer',
    syntax: 'ceiling(x)',
    example: 'ceiling(value)',
    summary: 'round up',
  },
  {
    name: 'exp',
    description: 'The standard exponential function, e^x',
    syntax: 'exp(x)',
    example: 'exp(value)',
    summary: 'natural exponent',
  },
  {
    name: 'floor',
    description: 'x, rounded down to an integer',
    syntax: 'floor(x)',
    example: 'floor(value)',
    summary: 'round down',
  },
  {
    name: 'ln',
    description: 'The natural (base-e) logarithm of x',
    syntax: 'ln(x)',
    example: 'ln(value)',
    summary: 'natural log',
  },
  {
    name: 'log',
    description: 'The logarithm of x in base y (defaults to 10)',
    syntax: ['log(x)', 'log(x, y)'],
    example: ['log(value)', 'log(value, 8)'],
    summary: 'logarithm',
  },
  {
    name: 'max',
    description: 'The larger of x and y',
    syntax: 'max(x, y)',
    example: 'max(readBytes, writeBytes)',
    summary: 'maximum of two numbers',
  },
  {
    name: 'min',
    description: 'The smaller of x and y',
    syntax: 'min(x, y)',
    example: 'min(readBytes, writeBytes)',
    summary: 'minimum of two numbers',
  },
  {
    name: 'pow',
    description: 'x to the power of y, x^y',
    syntax: 'pow(x, y)',
    example: 'pow(cpuSeconds, 2)',
    summary: 'x to the power of y',
  },
  {
    name: 'sqrt',
    description: 'The square root of x',
    syntax: 'sqrt(x)',
    example: 'sqrt(cpuSeconds)',
    summary: 'square root',
  },
];

export const PowerQueryStringFunctionInfo: Array<HelpItem> = [
  {
    name: 'format',
    description:
      'Generate a formatted string, inserting values as specified. Each % character in the format string corresponds to one value to be inserted.',
    syntax: 'format(fmt, ...)',
    example: 'format("Processed %,d records for customer %s in %.3f seconds.")',
    summary: 'format a string',
  },
  {
    name: 'isblank',
    description: 'True if x is null, an empty string, or contains only whitespace.',
    syntax: 'isblank(x)',
    example: 'isblank(accountName)',
    summary: 'true if string is blank',
  },
  {
    name: 'isempty',
    description: 'True if x is null or an empty string.',
    syntax: 'isempty(x)',
    example: 'isempty(accountName)',
    summary: 'true if string is empty',
  },
  {
    name: 'len',
    description: 'The number of characters in x',
    syntax: 'len(x)',
    example: 'len(accountName)',
    summary: 'string length',
  },
  {
    name: 'lower',
    description: 'x, with all letters changed to lowercase',
    syntax: 'lower(x)',
    example: 'lower',
    summary: 'lowercase',
  },
  {
    name: 'ltrim',
    description: 'x, with leading whitespace or other characters (provided as a string) removed',
    syntax: ['ltrim(x)', 'ltrim(x, y)'],
    example: ['ltrim(accountName)', 'ltrim(accountName, ".-")'],
    summary: 'trim leading whitespace or other characters',
  },
  {
    name: 'replace',
    description:
      'x, with all matches for y replaced by z. y and z must be string constants: y specifies a regular expression, and z is a simple string. Matching is not case sensitive. y cannot be empty.',
    syntax: 'replace(x, y, z)',
    example: 'replace(userName, "^',
    summary: 'replace matches within a string',
  },
  {
    name: 'rtrim',
    description: 'x, with trailing whitespace or other characters (provided as a string) removed',
    syntax: ['rtrim(x)', 'rtrim(x, y)'],
    example: ['rtrim(accountName)', 'rtrim(accountName, ".-")'],
    summary: 'trim trailing whitespace or other characters',
  },
  {
    name: 'string',
    description:
      'x, as a string. This is primarily useful to force a large number to be displayed in "raw" form, without commas or other numeric formatting. Missing values will be rendered as null',
    syntax: 'string(x)',
    example: 'string(totalBytes)',
    summary: 'convert a value to a string',
  },
  {
    name: 'substr',
    description:
      'z characters of x, starting at position y. If y is greater than the length of x, the result is an empty string. If z is provided and is greater than the number of characters after position y, then the entire string starting at position y is returned.',
    syntax: ['substr(x, y)', 'substr(x, y, z)'],
    example: ['substr(accountName, 5)', 'substr(accountName, 5, 12)'],
    summary: 'select a sequence of characters from a string',
  },
  {
    name: 'trim',
    description: 'x, with leading and trailing whitespace or other characters (provided as a string) removed',
    syntax: ['trim(x)', 'trim(x, y)'],
    example: ['trim(accountName)', 'trim(accountName, ".-")'],
    summary: 'trim leading and trailing whitespace or other characters',
  },
  {
    name: 'upper',
    description: 'x, with all letters changed to uppercase',
    syntax: 'upper(x)',
    example: 'upper',
    summary: 'uppercase',
  },
];

export const PowerQueryAggregationFunctionInfo: Array<HelpItem> = [
  {
    name: 'overall_average',
    description: 'The average value of the input expression, across all rows.',
    syntax: 'overall_average(expression)',
    example: 'overall_average(latency)',
    summary: 'average across all rows',
  },
  {
    name: 'overall_avg',
    description: 'The average value of the input expression, across all rows.',
    syntax: 'overall_avg(expression)',
    example: 'overall_avg(latency)',
    summary: 'average across all rows',
  },
  {
    name: 'overall_count',
    description: 'The total number of rows.',
    syntax: 'overall_count()',
    example: 'overall_count()',
    summary: 'total number of rows',
  },
  {
    name: 'overall_max',
    description: 'The largest value of the input expression in any row.',
    syntax: 'overall_max(expression)',
    example: 'overall_max(latency)',
    summary: 'largest value across all rows',
  },
  {
    name: 'overall_mean',
    description: 'The average value of the input expression, across all rows.',
    syntax: 'overall_mean(expression)',
    example: 'overall_mean(latency)',
    summary: 'average across all rows',
  },
  {
    name: 'overall_min',
    description: 'The smallest value of the input expression in any row.',
    syntax: 'overall_min(expression)',
    example: 'overall_min(latency)',
    summary: 'smallest value across all rows',
  },
  {
    name: 'overall_sum',
    description: 'overall_sum',
    syntax: 'overall_sum(expression)',
    example: 'overall_sum(errorCount)',
    summary: 'sum across all rows',
  },
  {
    name: 'percent_of_total',
    description: 'The input expression, as a percentage of the sum of that expression across all rows.',
    syntax: 'percent_of_total(expression)',
    example: 'percent_of_total(errorCount)',
    summary: 'percent of the sum total across all rows',
  },
  {
    name: 'running_count',
    description: 'Row number: 1 for the first row, 2 for the second row, etc.',
    syntax: 'running_count()',
    example: 'running_count()',
    summary: 'row number',
  },
  {
    name: 'running_percent',
    description:
      'A running total of percent_of_total(). For instance, if the first three rows add up to 25% of the total in all rows, then in the third row this will be 25.',
    syntax: 'running_percent(expression)',
    example: 'running_percent(errorCount)',
    summary: 'percent of sum total through this row',
  },
  {
    name: 'running_sum',
    description: 'The sum of the input expression for all rows up to and including this row.',
    syntax: 'running_sum(expression)',
    example: 'running_sum(errorCount)',
    summary: 'sum through current row',
  },
];

export const PowerQueryTimeFunctionInfo: Array<HelpItem> = [
  {
    name: 'queryend',
    description: 'The end of the time range for the current query, in nanoseconds if a time unit is not provided.',
    syntax: ['queryend()', 'queryend(unit)'],
    example: ['queryend()', 'queryend("minutes")'],
    summary: 'end of time range',
  },
  {
    name: 'queryspan',
    description: 'The length of the time range for the current query, in nanoseconds if a time unit is not provided.',
    syntax: ['queryspan()', 'queryspan(unit)'],
    example: ['queryspan()', 'queryspan("minutes")'],
    summary: 'length of time range',
  },
  {
    name: 'querystart',
    description: 'The start of the time range for the current query, in nanoseconds if a time unit is not provided.',
    syntax: ['querystart()', 'querystart(unit)'],
    example: ['querystart()', 'querystart("minutes")'],
    summary: 'start of time range',
  },
  {
    name: 'timebucket',
    description:
      'Truncates a time value (which defaults to the timestamp of the current event) to the beginning of a bucket, rounded to the nearest unit (if provided). For instance, if unit is "1 hour", this function returns the timestamp rounded down to a one-hour boundary. The timestamp can be expressed as seconds, milliseconds, microseconds, or nanoseconds from 1/1/1970, and the result will be in the same units. The time unit can be "days", "hours", "minutes",  "seconds", or various abbreviations such as "5m" for 5 minutes; hour/day boundaries are based on GMT. Instead of a time unit, you can also provide a number between 1 and 500, in which case the time span of the query is divided into that many buckets. For instance, if you are querying from 7:00 AM to 11:00 AM, timebucket(timestamp, 8) will divide that time range into 8 buckets of 30 minutes each. Finally, if you specify a time unit of "auto", ' +
      K.PRODUCT_NAME +
      ' will automatically select a reasonable bucket size.',
    syntax: ['timebucket()', 'timebucket(unit)', 'timebucket(timestamp, unit)'],
    example: ['timebucket()', 'timebucket("5m")', 'timebucket(startTime, "1 hour")'],
    summary: 'set a time to the beginning of a bucket',
  },
];

export const PowerQueryGroupFunctionInfo: Array<HelpItem> = [
  {
    name: 'any',
    description: 'A non-null input (chosen arbitrarily). If there are no non-null inputs, the result is null.',
    syntax: 'any(expression)',
    example: 'any(accountName)',
    summary: 'an arbitrary input within the group',
  },
  {
    name: 'average',
    description:
      'The average of all inputs, ignoring null, NaN, or non-numeric inputs. If there are no eligible inputs, the result is NaN.',
    syntax: 'average(expression)',
    example: 'average(querySeconds)',
    summary: 'average of all inputs in the group',
  },
  {
    name: 'avg',
    description:
      'The average of all inputs, ignoring null, NaN, or non-numeric inputs. If there are no eligible inputs, the result is NaN.',
    syntax: 'avg(expression)',
    example: 'avg(querySeconds)',
    summary: 'average of all inputs in the group',
  },
  {
    name: 'count',
    description: 'Counts the number of records in the group, or for which the given expression is true.',
    syntax: ['count()', 'count(expression)'],
    example: ['count()', 'count(status = 200)'],
    summary: 'number of records in the group',
  },
  {
    name: 'estimate_distinct',
    description:
      'Estimates the number of distinct values in the group. Null is ignored, but NaN is counted as a value.',
    syntax: 'estimate_distinct(expression)',
    example: 'estimate_distinct',
    summary: 'estimated number of distinct values in the group',
  },
  {
    name: 'first',
    description: 'The first non-null input in the group. Input must be sorted.',
    syntax: 'first(expression)',
    example: 'first',
    summary: 'first non-null input in the group',
  },
  {
    name: 'last',
    description: 'The last non-null input in the group. Input must be sorted.',
    syntax: 'last(expression)',
    example: 'last',
    summary: 'last non-null input in the group',
  },
  {
    name: 'max',
    description:
      'The largest input, ignoring null, NaN, or non-numeric inputs. If there are no eligible inputs, the result is NaN.',
    syntax: 'max(expression)',
    example: 'max(latency)',
    summary: 'largest input in the group',
  },
  {
    name: 'mean',
    description:
      'The average of all inputs, ignoring null, NaN, or non-numeric inputs. If there are no eligible inputs, the result is NaN.',
    syntax: 'mean(expression)',
    example: 'mean(querySeconds)',
    summary: 'average of all inputs in the group',
  },
  {
    name: 'median',
    description:
      'The median input, ignoring null, NaN, or non-numeric inputs. If there are no eligible inputs, the result is NaN.',
    syntax: 'median(expression)',
    example: 'median(latency)',
    summary: 'median input in the group',
  },
  {
    name: 'min',
    description:
      'The smallest input, ignoring null, NaN, or non-numeric inputs. If there are no eligible inputs, the result is NaN.',
    syntax: 'min(expression)',
    example: 'min(latency)',
    summary: 'smallest input in the group',
  },
  {
    name: 'newest',
    description: 'The non-null input with the newest timestamp. Input must consist of raw events.',
    syntax: 'newest(expression)',
    example: 'newest(loginTime)',
    summary: 'most recent input in the group',
  },
  {
    name: 'oldest',
    description: 'The non-null input with the olddest timestamp. Input must consist of raw events.',
    syntax: 'oldest(expression)',
    example: 'oldest(loginTime)',
    summary: 'least recent input in the group',
  },
  {
    name: 'p10',
    description: 'The 10th percentile of the inputs.',
    syntax: 'p10(expression)',
    example: 'p10(latency)',
    summary: '10th percentile of inputs in the group',
  },
  {
    name: 'p50',
    description: 'The 50th percentile of the inputs.',
    syntax: 'p50(expression)',
    example: 'p50(latency)',
    summary: '50th percentile of inputs in the group',
  },
  {
    name: 'p90',
    description: 'The 90th percentile of the inputs.',
    syntax: 'p90(expression)',
    example: 'p90(latency)',
    summary: '90th percentile of inputs in the group',
  },
  {
    name: 'p95',
    description: 'The 95th percentile of the inputs.',
    syntax: 'p95(expression)',
    example: 'p95(latency)',
    summary: '95th percentile of inputs in the group',
  },
  {
    name: 'p99',
    description: 'The 99th percentile of the inputs.',
    syntax: 'p99(expression)',
    example: 'p99(latency)',
    summary: '99th percentile of inputs in the group',
  },
  {
    name: 'p999',
    description: 'The 99.9th percentile of the inputs.',
    syntax: 'p999(expression)',
    example: 'p999(latency)',
    summary: '99.9th percentile of inputs in the group',
  },
  {
    name: 'pct',
    description:
      'A percentile of the inputs, ignoring null, NaN, or non-numeric inputs. NN should be a number from 0 to 100. For instance,pct(99.9, latency) yields the 99.9th percentile of the "latency" field. If there are no eligible inputs, the result is NaN.',
    syntax: 'pct(NN, expression)',
    example: 'pct(80, latency)',
    summary: 'a percentile of inputs in the group',
  },
  {
    name: 'stddev',
    description:
      'The standard deviation of the inputs, ignoring null, NaN, or non-numeric inputs. If there are no eligible inputs, the result is NaN.',
    syntax: 'stddev(expression)',
    example: 'stddev(latency)',
    summary: 'standard deviation within the group',
  },
  {
    name: 'sum',
    description:
      'The sum of all inputs, ignoring null, NaN, or non-numeric inputs. If there are no eligible inputs, the result is 0.',
    syntax: 'sum(expression)',
    example: 'sum(bytesScanned)',
    summary: 'sum of all inputs in the group',
  },
];

export const PowerQueryNetworkFunctionInfo: Array<HelpItem> = [
  {
    name: 'net_ip',
    description: 'True if x is a valid IP address.',
    syntax: 'net_ip(x)',
    example: 'net_ip(ip)',
    summary: 'is IP address',
  },
  {
    name: 'net_ipv4',
    description: 'True if x is a valid IPV4 address.',
    syntax: 'net_ipv4(x)',
    example: 'net_ipv4(ip)',
    summary: 'is IPV4 address',
  },
  {
    name: 'net_ipv6',
    description: 'True if x is a valid IPV6 address.',
    syntax: 'net_ipv6(x)',
    example: 'net_ipv6(ip)',
    summary: 'is IPV6 address',
  },
  {
    name: 'net_ipsubnet',
    description: 'True if x is within the y subnet expression.',
    syntax: 'net_ipsubnet(x, y)',
    example: 'net_ipsubnet(src.ip, ip)',
    summary: 'is x in y subnet',
  },
  {
    name: 'net_private',
    description: 'True if x is a valid IPV4 or IPV6 private address.',
    syntax: 'net_private(x)',
    example: 'net_private(ip)',
    summary: 'is private IP address',
  },
  {
    name: 'net_rfc1918',
    description: 'True if x is a valid IPV4 private address.',
    syntax: 'net_rfc1918(x)',
    example: 'net_rfc1918(ip)',
    summary: 'is private IPV4 address',
  },
  {
    name: 'net_rfc4193',
    description: 'True if x is a valid IPV6 private address.',
    syntax: 'net_rfc4193(x)',
    example: 'net_rfc4193(ip)',
    summary: 'is private IPV6 address',
  },
];
